<template>
  <v-dialog :fullscreen="$vuetify.breakpoint.smAndDown" :value="value" content-class="acq-dialog" max-width="560" @input="reset">
    <v-card class="acq-dialog">
      <div class="d-flex align-center pa-5 pb-2" style="gap: 12px">
        <span class="acq-avatar acq-avatar--sm tone-info"><v-icon color="info" small>mdi-domain</v-icon></span>
        <div class="flex-grow-1" style="min-width: 0">
          <div class="acq-section-title text-truncate">{{ institution.name }}</div>
          <div class="acq-tiny">Der Standort steht in {{ listCountText }}. Wo möchtest du ihn öffnen?</div>
        </div>
        <button aria-label="Schließen" class="dash-icon-btn" v-tip="'Schließen'" type="button" @click="reset">
          <v-icon small>mdi-close</v-icon>
        </button>
      </div>

      <div class="pa-5 pt-3 d-flex flex-column" style="gap: 6px">
        <span class="acq-label">Standortprofil</span>
        <a :href="$router.resolve(`/institutions/${institution._id}`).href" class="link-row acq-link" target="_blank" @click="reset">
          <span class="dash-card__icon tone-muted"><v-icon small>mdi-domain</v-icon></span>
          <span class="flex-grow-1 font-weight-medium text-truncate">{{ institution.name }}</span>
          <v-icon small>mdi-open-in-new</v-icon>
        </a>

        <span class="acq-label mt-3">Akquiselisten</span>
        <button v-for="list in acquisitionLists" :key="String(list._id)" class="link-row" type="button" @click="openList(list)">
          <span class="dash-card__icon tone-teal"><v-icon small>mdi-format-list-checks</v-icon></span>
          <span class="flex-grow-1 text-left" style="min-width: 0">
            <span class="d-block font-weight-medium text-truncate">{{ list.name }}</span>
            <span class="acq-tiny d-flex flex-wrap align-center" style="gap: 4px 10px">
              <span v-if="list.forEmployee">für {{ personName(list.forEmployee) }}</span>
              <span>{{ (list.institutions || []).length }} Standorte</span>
            </span>
          </span>
          <initials-badge v-if="assigneeName(list)" :name="assigneeName(list)" title="Zuständig"/>
          <v-icon small>mdi-open-in-new</v-icon>
        </button>
      </div>

      <div class="d-flex align-center pa-5 pt-0">
        <v-spacer/>
        <button class="cta cta--ghost" type="button" @click="reset">Abbrechen</button>
      </div>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import Vue from 'vue';
import {Institution} from '@/interfaces/institution.interface';
import {AcquisitionList} from '@/documentTypes/institutioner/acquisition/AcquisitionList.document';
import {retrieveObjectId} from '@/helpers/isObjectId.helper';
import InitialsBadge from '@/components/common/modern/initialsBadge.vue';

/** Institution in active acquisition lists: open it in one of the lists or as profile (new tab) */
export default Vue.extend({
  components: {InitialsBadge},
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    acquisitionLists: {
      type: Array as () => AcquisitionList[],
      required: true,
    },
    institution: {
      type: Object as () => Institution,
      required: true,
    },
  },
  computed: {
    listCountText(): string {
      const count = this.acquisitionLists.length;
      return count === 1 ? 'einer aktiven Akquiseliste' : `${count} aktiven Akquiselisten`;
    },
  },
  methods: {
    personName(person: unknown): string {
      const p = person as { firstName?: string; lastName?: string } | null;
      return p && typeof p === 'object' ? `${p.firstName || ''} ${p.lastName || ''}`.trim() : '';
    },
    assigneeName(list: AcquisitionList): string {
      const assignee = list.assignee as unknown as { meta?: unknown } | null;
      return assignee && typeof assignee === 'object' ? this.personName(assignee.meta) : '';
    },
    openList(list: AcquisitionList) {
      const link = this.$router.resolve({
        path: `/acquisitionLists/${retrieveObjectId(list)}/${this.institution._id}`,
      }).href;
      window.open(link, '_blank');
      this.reset();
    },
    reset() {
      this.$emit('input', false);
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(v: boolean) {
        if (v && (!this.institution || !this.institution.name)) {
          this.$$showSnackbar('Fehler beim Laden der Daten.', 'error');
          this.reset();
        }
      },
    },
  },
});
</script>

<style scoped>
.link-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  border-radius: 12px;
  background: rgba(15, 23, 42, .03);
  color: inherit;
  font: inherit;
  cursor: pointer;
  text-decoration: none;
}

.link-row:hover {
  background: rgba(25, 118, 210, .08);
}

.link-row:focus-visible {
  outline: 2px solid #1976D2;
  outline-offset: 2px;
}

.theme--dark .link-row {
  background: rgba(255, 255, 255, .05);
}

.theme--dark .link-row:hover {
  background: rgba(144, 202, 249, .12);
}
</style>
<style src="@/components/acquisitionList/ui/acquisitionViews.css"></style>
<style src="@/components/common/modern/modern.css"></style>
<style src="@/components/dashboard/dashboard.css"></style>
